import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  LayoutDashboard, Package, ShoppingBag, Sparkles,
  Users, Store, Settings as SettingsIcon, Tag, Wallet, Banknote,
  Heart, BookmarkPlus, UserPlus, Scale, UserCog, MessageCircle,
  Landmark, HelpCircle, Plug, FileText,
  UserX, ChevronLeft, ChevronRight, Truck,
} from 'lucide-react';

/**
 * The visible tab strip is decided by the LOGGED-IN ROLE, not by the URL
 * prefix. Previously we were prefix-matching `/vendedor` which incorrectly
 * matched the buyer route `/vendedores-que-sigo`, swapping a buyer's tabs
 * for the seller's. Tab visibility now derives from `user.role` first; only
 * after that we use the path to highlight the active item.
 */
const SELLER_TABS = [
  { to: '/vendedor', label: 'Panel', icon: LayoutDashboard, end: true },
  { to: '/vendedor/productos', label: 'Productos', icon: Package },
  { to: '/vendedor/pedidos', label: 'Pedidos', icon: ShoppingBag },
  { to: '/vendedor/envios', label: 'Envíos', icon: Truck },
  { to: '/vendedor/mensajes', label: 'Mensajes', icon: MessageCircle },
  { to: '/vendedor/finanzas', label: 'Finanzas', icon: Landmark },
  { to: '/vendedor/suscripcion', label: 'Plan / Suscripción', icon: Sparkles },
  { to: '/perfil', label: 'Mi cuenta', icon: UserCog },
];

const ADMIN_TABS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/usuarios', label: 'Usuarios', icon: Users },
  { to: '/admin/vendedores', label: 'Vendedores', icon: Store },
  { to: '/admin/pedidos', label: 'Pedidos', icon: ShoppingBag },
  { to: '/admin/finanzas', label: 'Finanzas', icon: Landmark },
  { to: '/admin/pqr', label: 'PQR', icon: HelpCircle },
  { to: '/admin/eliminacion-cuentas', label: 'Eliminación de cuentas', icon: UserX },
  { to: '/admin/promociones', label: 'Promociones', icon: Tag },
  { to: '/admin/facturacion-dian', label: 'Facturación DIAN', icon: FileText },
  { to: '/admin/integraciones', label: 'Integraciones', icon: Plug },
  { to: '/admin/configuracion', label: 'Configuración', icon: SettingsIcon },
  { to: '/perfil', label: 'Mi cuenta', icon: UserCog },
];

const BUYER_TABS = [
  { to: '/mis-pedidos', label: 'Mis pedidos', icon: ShoppingBag, end: true },
  { to: '/mensajes', label: 'Mensajes', icon: MessageCircle },
  { to: '/favoritos', label: 'Favoritos', icon: Heart },
  { to: '/lista-de-deseos', label: 'Lista de deseos', icon: BookmarkPlus },
  { to: '/vendedores-que-sigo', label: 'Vendedores que sigo', icon: UserPlus },
  { to: '/comparar', label: 'Comparador', icon: Scale },
  { to: '/perfil', label: 'Mi cuenta', icon: UserCog },
  { to: '/catalogo', label: 'Seguir comprando', icon: Tag },
];

// URL prefixes where the dashboard nav should appear at all.
const ADMIN_ROUTES = ['/admin'];
const SELLER_ROUTES = ['/vendedor', '/perfil'];  // /vendedor (exact) and any /vendedor/...
const BUYER_ROUTES = [
  '/mis-pedidos', '/favoritos', '/lista-de-deseos', '/mensajes',
  '/vendedores-que-sigo', '/comparar', '/tienda/', '/perfil',
];

const matchesAny = (path, prefixes) =>
  prefixes.some((p) => path === p || path.startsWith(p === '/' ? p : p + '/') || path.startsWith(p));

const isSellerPath = (path) =>
  path === '/vendedor' || path.startsWith('/vendedor/') || path === '/perfil';

const isAdminPath = (path) => path === '/admin' || path.startsWith('/admin/');

// Flechas + degradados solo cuando hay módulos fuera de la vista; centra el activo.
const useScrollEdges = (ref, deps) => {
  const [edges, setEdges] = useState({ left: false, right: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, ...deps]);
  return edges;
};

const EdgeArrow = ({ side, onClick }) => {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <div className={`pointer-events-none absolute inset-y-0 ${side === 'left' ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l'} from-white via-white/90 to-transparent w-14 flex items-center ${side === 'left' ? 'justify-start' : 'justify-end'} z-10`}>
      <button type="button" onClick={onClick} aria-label={side === 'left' ? 'Ver módulos anteriores' : 'Ver más módulos'}
              data-testid={`dashnav-scroll-${side}`}
              className="pointer-events-auto h-8 w-8 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-700 hover:text-orange-600 hover:border-orange-300 transition-colors">
        <Icon className="h-4 w-4" strokeWidth={2.5} />
      </button>
    </div>
  );
};

export const DashboardNav = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const scroller = useRef(null);
  const path = loc.pathname;
  const edges = useScrollEdges(scroller, [path, user?.role]);

  useEffect(() => {
    const active = scroller.current?.querySelector('[aria-current="page"]');
    active?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [path, user?.role]);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  if (!user) return null;
  // Tabs are owned by ROLE. Path is only used to decide whether to render at all.
  let tabs = null;
  if (user.role === 'admin' && (isAdminPath(path) || path === '/perfil')) tabs = ADMIN_TABS;
  else if (user.role === 'seller' && isSellerPath(path)) tabs = SELLER_TABS;
  else if (user.role === 'buyer' && matchesAny(path, BUYER_ROUTES)) tabs = BUYER_TABS;
  if (!tabs) return null;

  return (
    <div className="border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 sticky top-0 z-30">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {edges.left && <EdgeArrow side="left" onClick={() => scrollBy(-1)} />}
        {edges.right && <EdgeArrow side="right" onClick={() => scrollBy(1)} />}
        <div ref={scroller} className="flex gap-2 overflow-x-auto py-3 no-scrollbar scroll-smooth" data-testid="dashboard-nav-tabs">
          {tabs.map((t) => {
            const active = t.end ? path === t.to : (path === t.to || path.startsWith(t.to + '/'));
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`dashnav-${t.to.replace(/\//g, '-').replace(/^-/, '')}`}
                aria-current={active ? 'page' : undefined}
                className={[
                  'group relative inline-flex items-center gap-2.5 whitespace-nowrap',
                  'px-4 py-2.5 rounded-lg text-sm font-semibold',
                  'transition-all duration-200 ease-out',
                  active
                    ? [
                        // Estado activo — chip naranja AutoMarket con sombra
                        'bg-orange-500 text-white shadow-md shadow-orange-500/25',
                        'ring-1 ring-orange-600/20',
                        'hover:bg-orange-600',
                      ].join(' ')
                    : [
                        // Estado normal — chip sutil con hover elevado
                        'bg-slate-50 text-slate-700 border border-slate-200/70',
                        'hover:bg-white hover:text-slate-900 hover:border-slate-300',
                        'hover:shadow-sm hover:-translate-y-[1px]',
                      ].join(' '),
                ].join(' ')}
              >
                <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-500 group-hover:text-orange-500'} transition-colors`} strokeWidth={2.25} />
                <span>{t.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
